import type { ReactNode } from 'react';
import { CardState } from '../../components/Card';
import { clsx } from '../../components/clsx';
import { classifyFearGreed, fearGreedLabel, type FearGreedTone } from '../../lib/bitcoin/fearGreed';
import { formatHalvingDistance, type HalvingEstimate } from '../../lib/bitcoin/halving';
import { formatInt, formatMonthYear, satsPerDollar } from '../../lib/bitcoin/units';
import type { FearGreedState } from './useBitcoinData';

/** Semantic tokens only — a hex literal here is a lint error by design. */
const TONE_CLASS: Readonly<Record<FearGreedTone, string>> = {
  negative: 'text-negative',
  warn: 'text-warn',
  neutral: 'text-ink',
  positive: 'text-positive',
};

function Metric({
  label,
  value,
  suffix,
  valueClass,
  title,
}: {
  label: string;
  value: ReactNode;
  suffix?: string;
  valueClass?: string;
  title?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col justify-center px-2 first:pl-0 last:pr-0" title={title}>
      <span className="truncate text-[8.5px] font-semibold uppercase tracking-[0.1em] text-ink-subtle">
        {label}
      </span>
      <span className="flex min-w-0 items-baseline gap-1">
        <span className={clsx('truncate font-mono text-[13px] font-semibold leading-tight', valueClass ?? 'text-ink')}>
          {value}
        </span>
        {suffix !== undefined && (
          <span className="truncate text-[9px] text-ink-subtle">{suffix}</span>
        )}
      </span>
    </div>
  );
}

export interface MicroStripProps {
  readonly price: number | null;
  readonly fearGreed: FearGreedState;
  readonly halving: HalvingEstimate;
}

/**
 * The 3-up strip: sats per dollar, fear & greed, next halving.
 *
 * Fear & greed is the one sub-section allowed to fail on its own. When it
 * does it renders `CardState variant="unavailable"` in place of its cell and
 * the other two — plus the price, the chart and the stack — stay healthy;
 * the card never drops to a whole-panel error for a decorative index.
 */
export function MicroStrip({ price, fearGreed, halving }: MicroStripProps) {
  const sats = price === null ? null : satsPerDollar(price);
  const reading = fearGreed.reading;

  return (
    <div className="grid shrink-0 grid-cols-3 divide-x divide-line">
      <Metric
        label="Sats / $"
        value={formatInt(sats)}
        suffix="sats"
        title="100,000,000 ÷ price"
      />

      {reading !== null ? (
        <Metric
          label="Fear & Greed"
          value={reading.value}
          suffix={fearGreedLabel(reading.value, reading.label)}
          valueClass={TONE_CLASS[classifyFearGreed(reading.value).tone]}
          title="Crypto Fear & Greed Index — alternative.me"
        />
      ) : fearGreed.status === 'error' ? (
        <div className="min-w-0 px-1">
          <CardState variant="unavailable" compact message="Fear & Greed" />
        </div>
      ) : (
        <Metric label="Fear & Greed" value="··" title="Loading the Fear & Greed Index" />
      )}

      <Metric
        label="Halving"
        value={formatHalvingDistance(halving.daysRemaining)}
        suffix={formatMonthYear(halving.etaMs)}
        title={
          `Block ${halving.targetHeight.toLocaleString()} — ` +
          `${halving.blocksRemaining.toLocaleString()} blocks to go, ` +
          `subsidy ${halving.subsidyNow} → ${halving.subsidyAfter} BTC. ` +
          (halving.source === 'network'
            ? `Tip height from mempool.space, ${(halving.blockMs / 60000).toFixed(1)} min/block.`
            : 'Height projected offline from the 2024 halving at 10 min/block.')
        }
      />
    </div>
  );
}
